import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const FIELD_TYPES = [
  { id: "text", label: "Short answer" },
  { id: "textarea", label: "Paragraph" },
  { id: "date", label: "Date" },
  { id: "select", label: "Dropdown" },
  { id: "checkbox", label: "Checkbox / acknowledgement" },
  { id: "email", label: "Email" },
  { id: "tel", label: "Phone" },
  { id: "number", label: "Number" },
];

const input = "mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";

function emptyForm() {
  return { title: "", description: "", fields: [{ id: "", label: "", type: "text", required: false, options: [] }] };
}

function FieldEditor({ field, index, total, onChange, onRemove, onMove }) {
  const set = (k, v) => onChange({ ...field, [k]: v });
  return (
    <div data-testid={`form-field-editor-${index}`} className="rounded-md border border-linen p-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
        <div>
          <label className={label}>Question / statement</label>
          <textarea data-testid={`field-label-${index}`} rows={2} value={field.label} onChange={(e) => set("label", e.target.value)} className={input} />
        </div>
        <div>
          <label className={label}>Type</label>
          <select data-testid={`field-type-${index}`} value={field.type} onChange={(e) => set("type", e.target.value)} className={input}>
            {FIELD_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      </div>
      {field.type === "select" && (
        <div className="mt-3">
          <label className={label}>Options (one per line)</label>
          <textarea data-testid={`field-options-${index}`} rows={3} value={(field.options || []).join("\n")} onChange={(e) => set("options", e.target.value.split("\n"))} className={input} />
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-forest">
          <input data-testid={`field-required-${index}`} type="checkbox" checked={field.required} onChange={(e) => set("required", e.target.checked)} className="h-4 w-4 accent-forest" /> Required
        </label>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="rounded p-1.5 text-moss hover:text-forest disabled:opacity-30" aria-label="Move up"><ArrowUp size={15} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="rounded p-1.5 text-moss hover:text-forest disabled:opacity-30" aria-label="Move down"><ArrowDown size={15} /></button>
          <button type="button" data-testid={`field-remove-${index}`} onClick={onRemove} disabled={total === 1} className="rounded p-1.5 text-moss hover:text-destructive disabled:opacity-30" aria-label="Remove"><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}

function FormEditor({ form, isNew, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(form);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(form), [form]);

  const updateField = (i, f) => setDraft((d) => ({ ...d, fields: d.fields.map((x, j) => (j === i ? f : x)) }));
  const removeField = (i) => setDraft((d) => ({ ...d, fields: d.fields.filter((_, j) => j !== i) }));
  const moveField = (i, dir) => setDraft((d) => {
    const fields = [...d.fields];
    const [item] = fields.splice(i, 1);
    fields.splice(i + dir, 0, item);
    return { ...d, fields };
  });
  const addField = () => setDraft((d) => ({ ...d, fields: [...d.fields, { id: "", label: "", type: "text", required: false, options: [] }] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { title: draft.title, description: draft.description, fields: draft.fields.map((f) => ({ ...f, options: f.options || [] })) };
      const { data } = isNew ? await api.post("/admin/forms", payload) : await api.put(`/admin/forms/${form.form_type}`, payload);
      toast.success(isNew ? "Form created" : "Form saved");
      onSaved(data);
    } catch (e) {
      toast.error(apiError(e, "Could not save the form."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"? It will be removed from every service.`)) return;
    try {
      await api.delete(`/admin/forms/${form.form_type}`);
      toast.success("Form deleted");
      onDeleted(form.form_type);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="form-editor" className="rounded-lg border border-linen bg-ivory p-6">
      <div>
        <label className={label}>Form title</label>
        <input data-testid="form-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={input} />
      </div>
      <div className="mt-4">
        <label className={label}>Intro / legal text shown at the top</label>
        <textarea data-testid="form-description-input" rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className={input} />
      </div>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.15em] text-moss">Questions</p>
      <div className="mt-3 space-y-3">
        {draft.fields.map((f, i) => (
          <FieldEditor key={i} field={f} index={i} total={draft.fields.length} onChange={(nf) => updateField(i, nf)} onRemove={() => removeField(i)} onMove={(dir) => moveField(i, dir)} />
        ))}
      </div>
      <button type="button" data-testid="form-add-field" onClick={addField} className="mt-4 flex items-center gap-2 rounded-md border border-linen px-4 py-2 text-sm font-semibold text-forest hover:border-forest"><Plus size={15} /> Add question</button>
      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-linen pt-5">
        <button type="button" data-testid="form-save-button" onClick={save} disabled={busy || !draft.title.trim() || draft.fields.some((f) => !f.label.trim())} className={btn}>{busy ? "Saving..." : isNew ? "Create Form" : "Save Changes"}</button>
        {!isNew && <a href={`/forms/${form.form_type}`} target="_blank" rel="noreferrer" data-testid="form-preview-link" className="text-sm font-semibold text-moss hover:text-forest">Preview &rarr;</a>}
        {!isNew && <button type="button" data-testid="form-delete-button" onClick={remove} className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-destructive/80 hover:text-destructive"><Trash2 size={14} /> Delete form</button>}
      </div>
    </div>
  );
}

function ServiceMapping({ forms }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/service-forms").then((res) => setRows(res.data)), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (row, ft) => {
    const next = row.form_types.includes(ft) ? row.form_types.filter((x) => x !== ft) : [...row.form_types, ft];
    setRows((rs) => rs.map((r) => (r.service_id === row.service_id ? { ...r, form_types: next } : r)));
    try {
      await api.put(`/admin/service-forms/${row.service_id}`, { form_types: next });
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  return (
    <div data-testid="service-forms-mapping" className="overflow-x-auto rounded-lg border border-linen bg-ivory">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-linen text-xs uppercase tracking-[0.1em] text-moss">
          <tr>
            <th className="px-4 py-3">Service booked</th>
            {forms.map((f) => <th key={f.form_type} className="px-3 py-3 text-center">{f.title}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-linen">
          {rows.map((r) => (
            <tr key={r.service_id} data-testid={`service-forms-row-${r.service_id}`}>
              <td className="px-4 py-3">
                <p className="font-medium text-forest">{r.service_name}</p>
                <p className="text-xs text-moss">{r.category}</p>
              </td>
              {forms.map((f) => (
                <td key={f.form_type} className="px-3 py-3 text-center">
                  <input data-testid={`service-form-toggle-${r.service_id}-${f.form_type}`} type="checkbox" checked={r.form_types.includes(f.form_type)} onChange={() => toggle(r, f.form_type)} className="h-4 w-4 accent-forest" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FormBuilderTab() {
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState("forms");

  const load = useCallback(() => api.get("/admin/forms").then((res) => {
    setForms(res.data);
    setSelected((s) => s || res.data[0] || null);
  }), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="form-builder-tab">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-serif text-3xl font-medium text-forest">Form Builder</h2>
          <p className="mt-1 text-sm text-moss">Edit the legal forms clients sign, then choose which forms go out automatically for each service.</p>
        </div>
        <div className="flex rounded-md border border-linen p-1 text-sm font-semibold">
          <button data-testid="form-builder-view-forms" onClick={() => setView("forms")} className={`rounded px-4 py-1.5 ${view === "forms" ? "bg-forest text-ivory" : "text-moss"}`}>Edit Forms</button>
          <button data-testid="form-builder-view-mapping" onClick={() => setView("mapping")} className={`rounded px-4 py-1.5 ${view === "mapping" ? "bg-forest text-ivory" : "text-moss"}`}>Forms per Service</button>
        </div>
      </div>

      {view === "mapping" ? (
        <div className="mt-6"><ServiceMapping forms={forms} /></div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
          <div className="space-y-1">
            {forms.map((f) => (
              <button key={f.form_type} data-testid={`form-select-${f.form_type}`} onClick={() => { setSelected(f); setCreating(false); }} className={`block w-full rounded-md px-4 py-2.5 text-left text-sm transition-colors ${!creating && selected?.form_type === f.form_type ? "bg-forest text-ivory" : "text-forest hover:bg-cream"}`}>
                {f.title}
                <span className={`block text-xs ${!creating && selected?.form_type === f.form_type ? "text-ivory/70" : "text-moss"}`}>{f.fields.length} questions</span>
              </button>
            ))}
            <button data-testid="form-new-button" onClick={() => setCreating(true)} className={`mt-2 flex w-full items-center gap-2 rounded-md border border-dashed border-linen px-4 py-2.5 text-sm font-semibold ${creating ? "border-forest text-forest" : "text-moss hover:border-forest hover:text-forest"}`}><Plus size={15} /> New form</button>
          </div>
          <div>
            {creating ? (
              <FormEditor form={emptyForm()} isNew onSaved={(f) => { setCreating(false); load().then(() => setSelected(f)); }} onDeleted={() => {}} />
            ) : selected ? (
              <FormEditor form={selected} onSaved={(f) => { setSelected(f); load(); }} onDeleted={() => { setSelected(null); load(); }} />
            ) : (
              <p className="text-sm text-moss">Select a form to edit.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
